import { Card } from "@/components/ui/Card";
import { VoteButtons } from "@/components/ui/VoteButtons";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { MealTagBadge } from "@/components/ui/MealTagBadge";
import type { MealSuggestion } from "@/lib/models";
import { Link } from "react-router-dom";

const MEAL_TYPE_LABELS: Record<string, string> = {
  packed_lunch: "Packed Lunch",
  breakfast: "Breakfast",
  lunch: "Lunch",
  dinner: "Dinner",
};

const PICK_BADGE_LABELS: Record<string, string> = {
  breakfast: "Today's pick",
  packed_lunch: "Today's pick",
  lunch: "Today's pick",
  dinner: "Tonight's pick",
};

interface MealCardProps {
  meal: MealSuggestion;
  userId?: string;
  onVote: (meal: MealSuggestion, isUpvote: boolean) => void;
  /** Compact variant for dashboard grid */
  variant?: "compact" | "full";
  /** Full variant extras */
  isAdult?: boolean;
  mealType?: string;
  ingredientsExpanded?: boolean;
  instructionsExpanded?: boolean;
  onToggleIngredients?: () => void;
  onToggleInstructions?: () => void;
  onChoose?: (meal: MealSuggestion) => void;
  onDelete?: (meal: MealSuggestion) => void;
  canDelete?: boolean;
}

export function MealCard({
  meal,
  userId,
  onVote,
  variant = "full",
  isAdult,
  mealType,
  ingredientsExpanded,
  instructionsExpanded,
  onToggleIngredients,
  onToggleInstructions,
  onChoose,
  onDelete,
  canDelete,
}: MealCardProps) {
  const votes = meal.votes ?? {};
  const upvotes = Object.values(votes).filter((v) => v === true).length;
  const downvotes = Object.values(votes).filter((v) => v === false).length;
  const userVote = userId ? votes[userId] : undefined;
  const isFavorite = !meal.is_chosen && upvotes > downvotes && upvotes > 0;

  const pickLabel = PICK_BADGE_LABELS[meal.meal_type] ?? "Today's pick";

  if (variant === "compact") {
    return (
      <Card>
        <Link to="/meals" className="block">
          <div className="space-y-1.5">
            <div className="flex items-start justify-between gap-1">
              <p className="font-semibold text-xs line-clamp-2">{meal.title}</p>
              {meal.is_chosen ? (
                <StatusBadge text={pickLabel} preset="won" />
              ) : isFavorite ? (
                <StatusBadge text="★ Favorite" preset="custom" />
              ) : null}
            </div>
            {meal.description && (
              <p className="text-xs text-gray-400 line-clamp-1">{meal.description}</p>
            )}
            {meal.tags && meal.tags.length > 0 && (
              <div className="flex flex-wrap gap-1">
                {meal.tags.slice(0, 2).map((tag) => (
                  <MealTagBadge key={tag} text={tag} />
                ))}
              </div>
            )}
            <div className="flex items-center gap-2 text-xs text-gray-400">
              <span className="capitalize">{meal.meal_type.replace(/_/g, " ")}</span>
              {meal.calories_per_serving != null && meal.calories_per_serving > 0 && (
                <span>~{meal.calories_per_serving} cal/serving</span>
              )}
            </div>
          </div>
        </Link>
        <div className="mt-2 pt-1.5 border-t border-gray-100 dark:border-white/10">
          <VoteButtons
            upvotes={upvotes}
            downvotes={downvotes}
            userVote={userVote}
            onVote={(isUp) => onVote(meal, isUp)}
          />
        </div>
      </Card>
    );
  }

  // Full variant
  return (
    <Card>
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-bold text-sm">{meal.title}</h3>
          {meal.is_chosen ? (
            <StatusBadge text={pickLabel} preset="chosen" />
          ) : isFavorite ? (
            <StatusBadge text="★ Favorite" preset="custom" />
          ) : null}
        </div>

        <div className="flex flex-wrap gap-1">
          {meal.source === "custom" && <StatusBadge text="Custom" preset="custom" />}
          {meal.source === "takeout" && meal.cuisine_type && (
            <StatusBadge text={meal.cuisine_type} preset="takeout" />
          )}
          {meal.tags?.map((tag) => <MealTagBadge key={tag} text={tag} />)}
        </div>

        {meal.description && (
          <p className="text-sm text-gray-600 dark:text-gray-400">{meal.description}</p>
        )}

        <div className="flex items-center gap-3 text-xs text-gray-400">
          {meal.prep_time && (
            <span className="flex items-center gap-1">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              {meal.prep_time}
            </span>
          )}
          {meal.servings != null && (
            <span>{meal.servings} serving{meal.servings !== 1 ? "s" : ""}</span>
          )}
          {meal.calories_per_serving != null && meal.calories_per_serving > 0 && (
            <span>~{meal.calories_per_serving} cal/serving</span>
          )}
          {meal.estimated_cost != null && <span>~${meal.estimated_cost.toFixed(2)}</span>}
        </div>

        {/* Expandable ingredients */}
        {meal.ingredients && meal.ingredients.length > 0 && onToggleIngredients && (
          <div>
            <button
              onClick={onToggleIngredients}
              className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${ingredientsExpanded ? "rotate-90" : ""}`}>
                <polyline points="9 18 15 12 9 6" />
              </svg>
              Ingredients ({meal.ingredients.length})
            </button>
            {ingredientsExpanded && (
              <ul className="mt-1 ml-4 space-y-0.5">
                {meal.ingredients.map((ing, i) => (
                  <li key={i} className="text-xs text-gray-500">{ing}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Expandable instructions */}
        {meal.instructions && meal.instructions.length > 0 && onToggleInstructions && (
          <div>
            <button
              onClick={onToggleInstructions}
              className="flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-gray-700 transition-colors"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${instructionsExpanded ? "rotate-90" : ""}`}>
                <polyline points="9 18 15 12 9 6" />
              </svg>
              Instructions ({meal.instructions.length} steps)
            </button>
            {instructionsExpanded && (
              <ol className="mt-1 ml-4 space-y-1 list-decimal list-inside">
                {meal.instructions.map((step, i) => (
                  <li key={i} className="text-xs text-gray-500">{step}</li>
                ))}
              </ol>
            )}
          </div>
        )}

        {/* Actions row */}
        <div className="flex items-center gap-2 pt-1">
          {canDelete && onDelete && (
            <button
              onClick={() => onDelete(meal)}
              className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
            </button>
          )}

          <VoteButtons
            upvotes={upvotes}
            downvotes={downvotes}
            userVote={userVote}
            onVote={(isUp) => onVote(meal, isUp)}
            size="md"
          />

          <div className="flex-1" />

          {isAdult && onChoose && mealType && (
            <button
              onClick={() => onChoose(meal)}
              className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                meal.is_chosen
                  ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                  : "text-white bg-[color:var(--accent-color)] hover:opacity-90"
              }`}
            >
              {meal.is_chosen
                ? "Chosen ✓"
                : `Make this our winning ${(MEAL_TYPE_LABELS[mealType] ?? mealType).toLowerCase()}`}
            </button>
          )}
        </div>
      </div>
    </Card>
  );
}
